import type { PhoneNumber } from '@1trading/fast-api-domain/forms';
import { normaliseText } from '@1trading/ts-common/utils';
import { z } from 'zod';

import type { BinaryAnswer } from '@/constants/answers';
import {
  INSTITUTION_ADDRESS_DEFAULTS,
  type InstitutionAddress,
  institutionAddressSchema,
  institutionAddressToInstitutionDetailsAddressApi,
  institutionDetailsAddressApiToInstitutionAddress,
} from '@/features/institution/schemas/address';
import {
  createTaxResidencesSchema,
  TAX_RESIDENCE_DEFAULTS,
  type TaxResidence,
  taxResidenceApiToTaxResidence,
  taxResidenceToTaxResidenceApi,
} from '@/features/tax-residency/schema';
import i18n from '@/lib/i18n';
import { type InstitutionDetailsV2ContactablePerson, institutionDetailsV2ContactablePersonSchema } from '@/services/api/profile';
import { type DateParts, EMPTY_DATE_PARTS, isDatePartsValid, toDatePartsFromString, toDateStringFromParts } from '@/utils/date';
import { EMPTY_PHONE_NUMBER } from '@/utils/phone-number';

export type InstitutionPerson = {
  partyId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: DateParts;
  countryOfBirth: string;
  localityOfBirth: string;
  address: InstitutionAddress;
  nationality: string;
  email: string;
  phoneNumber: PhoneNumber;
  taxResidences: TaxResidence[];
};

export const institutionPersonSchema = z.object({
  partyId: institutionDetailsV2ContactablePersonSchema.shape.partyId,
  firstName: institutionDetailsV2ContactablePersonSchema.shape.firstName,
  lastName: institutionDetailsV2ContactablePersonSchema.shape.lastName,
  dateOfBirth: z
    .object({
      day: z.string().regex(/^\d{1,2}$/, i18n.t('institutionDetails.person.errors.dateOfBirth')),
      month: z.string().regex(/^\d{1,2}$/, i18n.t('institutionDetails.person.errors.dateOfBirth')),
      year: z.string().regex(/^\d{4}$/, i18n.t('institutionDetails.person.errors.dateOfBirth')),
    })
    .refine(isDatePartsValid, { error: i18n.t('institutionDetails.person.errors.dateOfBirth'), when: ({ issues }) => issues.length === 0 }),
  countryOfBirth: institutionDetailsV2ContactablePersonSchema.shape.countryOfBirth,
  localityOfBirth: institutionDetailsV2ContactablePersonSchema.shape.localityOfBirth,
  address: institutionAddressSchema,
  nationality: institutionDetailsV2ContactablePersonSchema.shape.nationality,
  email: institutionDetailsV2ContactablePersonSchema.shape.email,
  phoneNumber: institutionDetailsV2ContactablePersonSchema.shape.phoneNumber,
  taxResidences: createTaxResidencesSchema('individual'),
});

export const createInstitutionPerson = (): InstitutionPerson => ({
  partyId: crypto.randomUUID(),
  firstName: '',
  lastName: '',
  dateOfBirth: EMPTY_DATE_PARTS,
  countryOfBirth: '',
  localityOfBirth: '',
  address: INSTITUTION_ADDRESS_DEFAULTS,
  nationality: '',
  email: '',
  phoneNumber: EMPTY_PHONE_NUMBER,
  taxResidences: [TAX_RESIDENCE_DEFAULTS],
});

export const institutionPersonToInstitutionDetailsPersonApi = ({
  partyId,
  firstName,
  lastName,
  dateOfBirth,
  countryOfBirth,
  localityOfBirth,
  address,
  nationality,
  email,
  phoneNumber,
  taxResidences,
}: InstitutionPerson): InstitutionDetailsV2ContactablePerson => ({
  partyId,
  firstName,
  lastName,
  dateOfBirth: toDateStringFromParts(dateOfBirth),
  countryOfBirth,
  localityOfBirth,
  address: institutionAddressToInstitutionDetailsAddressApi(address),
  nationality,
  email,
  phoneNumber,
  taxResidences: taxResidences.map(taxResidenceToTaxResidenceApi),
});

export const institutionDetailsPersonApiToInstitutionPerson = ({
  partyId,
  firstName,
  lastName,
  dateOfBirth,
  countryOfBirth,
  localityOfBirth,
  address,
  nationality,
  email,
  phoneNumber,
  taxResidences,
}: InstitutionDetailsV2ContactablePerson): InstitutionPerson => ({
  partyId,
  firstName,
  lastName,
  dateOfBirth: toDatePartsFromString(dateOfBirth),
  countryOfBirth,
  localityOfBirth,
  address: institutionDetailsAddressApiToInstitutionAddress(address),
  nationality,
  email,
  // A person saved with an E.164 `phone` has no phoneNumber, which no field would report as missing
  phoneNumber: phoneNumber ?? EMPTY_PHONE_NUMBER,
  taxResidences: taxResidences.map(taxResidenceApiToTaxResidence),
});

export type InstitutionPersonInRole = InstitutionPerson & { isPep?: BinaryAnswer };

// What one person can't hold twice over; contact details can rightly differ, such as a work and a personal email
const SHARED_PERSON_DETAILS = ['firstName', 'lastName', 'dateOfBirth', 'countryOfBirth', 'localityOfBirth', 'nationality', 'address', 'taxResidences', 'isPep'] as const;

type SharedPersonDetail = (typeof SHARED_PERSON_DETAILS)[number];

const comparable = (text: string | undefined) => normaliseText(text ?? '').toLocaleLowerCase();

// An identifier reads the same with or without its separators, such as a postcode's space or a TIN's dots
const comparableIdentifier = (text: string | undefined) => comparable(text).replace(/[^\p{L}\p{N}]/gu, '');

const comparableDetail = (person: InstitutionPersonInRole, detail: SharedPersonDetail) => {
  switch (detail) {
    case 'dateOfBirth':
      return [person.dateOfBirth?.day, person.dateOfBirth?.month, person.dateOfBirth?.year].map(Number).join('-');
    case 'address':
      return [
        comparable(person.address?.addressLine1),
        comparable(person.address?.addressLine2),
        comparable(person.address?.locality),
        comparableIdentifier(person.address?.postalCode),
        person.address?.country,
      ].join('|');
    case 'taxResidences':
      return (person.taxResidences ?? [])
        .map(({ country, taxId }) => `${country}:${comparableIdentifier(taxId)}`)
        .sort()
        .join('|');
    default:
      return comparable(person[detail]);
  }
};

/** The shared details in which a copied person differs from the entry they were copied from */
export const getDifferingPersonDetails = (person: InstitutionPersonInRole, source: InstitutionPersonInRole) =>
  SHARED_PERSON_DETAILS.filter((detail) => comparableDetail(person, detail) !== comparableDetail(source, detail));

export const withSharedPersonDetailsOf = <Person extends InstitutionPersonInRole>(person: Person, source: InstitutionPersonInRole): Person => ({
  ...person,
  ...Object.fromEntries(SHARED_PERSON_DETAILS.map((detail) => [detail, structuredClone(source[detail])])),
});
